"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowLeft, ArrowRight, ChevronRight, PanelLeft } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DOCS_BASE,
  getBreadcrumb,
  getPrevNext,
} from "@/app/(marketing)/docs/_config";
import { SidebarNav } from "@/components/docs/sidebar-nav";
import { TableOfContents } from "@/components/docs/table-of-contents";

export function DocsShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const crumb = getBreadcrumb(pathname);
  const { prev, next } = getPrevNext(pathname);

  return (
    <div className="mx-auto max-w-[88rem] px-4 pt-8 pb-16 md:px-6">
      {/* Mobile menu trigger */}
      <div className="mb-5 lg:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="border-rl-border bg-rl-bg2 text-rl-text2 hover:text-rl-text inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[0.8125rem] font-medium transition-colors"
            >
              <PanelLeft size={15} aria-hidden />
              Browse documentation
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="border-rl-border bg-rl-bg2 w-[20rem] max-w-[85vw] overflow-y-auto p-6"
          >
            <SheetTitle className="text-rl-text mb-5 text-base font-bold">
              Documentation
            </SheetTitle>
            <SidebarNav onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid grid-cols-1 gap-x-10 gap-y-0 lg:grid-cols-[15rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)_13rem]">
        {/* Left sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-[4.5rem] max-h-[calc(100dvh-6rem)] overflow-y-auto pr-2 pb-8">
            <SidebarNav />
          </div>
        </aside>

        {/* Article column */}
        <div className="min-w-0">
          {crumb ? (
            <nav
              aria-label="Breadcrumb"
              className="text-rl-text3 mb-5 flex flex-wrap items-center gap-1.5 text-[0.8rem]"
            >
              <Link href={DOCS_BASE} className="hover:text-rl-text2">
                Docs
              </Link>
              <ChevronRight size={13} aria-hidden />
              <span>{crumb.sectionTitle}</span>
              <ChevronRight size={13} aria-hidden />
              <span className="text-rl-text2">{crumb.pageTitle}</span>
            </nav>
          ) : null}

          <article data-docs-article className="min-w-0">
            {children}
          </article>

          {prev || next ? (
            <div className="border-rl-border mt-14 grid gap-3 border-t pt-6 sm:grid-cols-2">
              {prev ? (
                <Link
                  href={prev.path}
                  className="surface-card surface-card-interactive group rounded-xl p-4"
                >
                  <span className="text-rl-text3 flex items-center gap-1 text-[0.75rem]">
                    <ArrowLeft size={13} aria-hidden /> Previous
                  </span>
                  <span className="text-rl-text group-hover:text-rl-accent mt-1 block font-semibold transition-colors">
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <span aria-hidden />
              )}
              {next ? (
                <Link
                  href={next.path}
                  className="surface-card surface-card-interactive group rounded-xl p-4 text-right sm:col-start-2"
                >
                  <span className="text-rl-text3 flex items-center justify-end gap-1 text-[0.75rem]">
                    Next <ArrowRight size={13} aria-hidden />
                  </span>
                  <span className="text-rl-text group-hover:text-rl-accent mt-1 block font-semibold transition-colors">
                    {next.title}
                  </span>
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Right table of contents */}
        <aside className="hidden xl:block">
          <div className="sticky top-[4.5rem] max-h-[calc(100dvh-6rem)] overflow-y-auto">
            <TableOfContents />
          </div>
        </aside>
      </div>
    </div>
  );
}
